import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import CustomerModal, { type Customer } from './CustomerModal'
import CountrySelect from '../../components/CountrySelect'
import CustomerAddressFields from '../../components/CustomerAddressFields'
import { ADDR_CDS_LIMITS, ADDR_TYPE_META, emptyAddressDraft, addressSameKey, sortRoles } from '../../lib/customerAddress'
import { countryLabelForCode } from '../../lib/countries'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../hooks/useToast'
import BoxtopAccountPanel from '../../components/BoxtopAccountPanel'
import { useSyncToBoxtrax, type BoxtopAmbiguousParty } from '../../hooks/useSyncToBoxtrax'
import BoxtopContactMatchDialog from '../../components/BoxtopContactMatchDialog'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'
import PoaStatusBadge from '../../components/PoaStatusBadge'
import { useAutosaveText } from '../../hooks/useAutosaveText'
import AutosaveStatusLabel from '../../components/AutosaveStatusLabel'
import { fetchPoaStatusForEori, formatPoaStatusLine, type PoaStatus } from '../../lib/poaStatus'

interface AddressType {
  id: string
  code: string
  description: string
  sort_order: number
}

interface CustomerContact {
  id: string
  customer_id: string
  name: string
  role: string
  email: string | null
  phone: string | null
  mobile: string | null
  notes: string | null
  is_primary: boolean
  sort_order: number
}

interface CustomerAddress {
  id: string
  customer_id: string
  address_type: string
  roles: string[]
  primary_roles: string[]
  label: string | null
  is_default: boolean
  address_lines: string[]
  city: string | null
  county: string | null
  postcode: string | null
  country: string
  contact_name: string | null
  contact_phone: string | null
  contact_email: string | null
  notes: string | null
  finance_approved: boolean
}

type ContactRole = 'Sales' | 'Finance' | 'Operations' | 'Warehouse' | 'General'
const CONTACT_ROLES: ContactRole[] = ['Sales', 'Finance', 'Operations', 'Warehouse', 'General']


const CHIP_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-yellow-100 text-yellow-700 border-yellow-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-indigo-100 text-indigo-700 border-indigo-200',
  'bg-teal-100 text-teal-700 border-teal-200',
  'bg-red-100 text-red-700 border-red-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-gray-100 text-gray-700 border-gray-200',
]

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500'

const emptyContact = (): Partial<CustomerContact> => ({
  name: '', role: 'General', email: '', phone: '', mobile: '', notes: '', is_primary: false,
})

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { userRoles } = useAuth()
  const { toast } = useToast()
  const location = useLocation()
  // New Company saved the company but NOT its first address (see CustomerModal): say so, loudly, once.
  useEffect(() => {
    const msg = (location.state as { addressNotSaved?: string } | null)?.addressNotSaved
    if (msg) toast(`Company created, but its address was NOT saved (${msg}). Please add the address below.`, 'error')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { matchContact } = useSyncToBoxtrax()
  // Z24: manual "Search BoxTop" pre-associate flow
  const [searchingBoxtop, setSearchingBoxtop] = useState(false)
  const [boxtopMatchParties, setBoxtopMatchParties] = useState<BoxtopAmbiguousParty[] | null>(null)
  const canApproveFinance = userRoles.includes('ADMIN') || userRoles.includes('ACCOUNTS')

  const [customer, setCustomer] = useState<Customer & { reminder?: string; finance?: Record<string, any>; boxtop_account_number?: string | null; boxtop_account_check_status?: 'ok' | 'failed' | null; boxtop_account_checked_number?: string | null; boxtop_account_checked_at?: string | null; boxtop_account_checked_by?: string | null; boxtop_account_company_name?: string | null; boxtop_account_address_count?: number | null; boxtop_account_name_confirmed_number?: string | null; boxtop_account_name_confirmed_at?: string | null; boxtop_account_name_confirmed_by?: string | null; boxtop_account_name_confirmed_boxtop_name?: string | null } | null>(null)
  const [allTypes, setAllTypes] = useState<AddressType[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [originalIds, setOriginalIds] = useState<Set<string>>(new Set())
  const [contacts, setContacts] = useState<CustomerContact[]>([])
  const [loading, setLoading] = useState(true)
  const [savingTypes, setSavingTypes] = useState(false)
  const [typeSaved, setTypeSaved] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  // Z73.6.1: quick indicator -- POA validity for this customer's own EORI (not job-specific).
  const [poaStatus, setPoaStatus] = useState<PoaStatus | null>(null)

  // Reminder
  // Autosaves via the shared hook; unlike the old saveReminder this surfaces DB errors instead of
  // showing "Saved" after a silently failed update.
  const reminder = useAutosaveText('', async v => {
    const { error } = await supabase.from('customers').update({ reminder: v.trim() || null }).eq('id', id!)
    if (error) throw error
  })

  // Finance
  const [finance, setFinance] = useState<Record<string, string>>({})
  const [savingFinance, setSavingFinance] = useState(false)
  const [financeSaved, setFinanceSaved] = useState(false)
  const [financeEditing, setFinanceEditing] = useState(false)

  // Z73.19: Descartes-only fields, added directly to customers (no more na_registry --
  // declaration generation now reads customers.code as SHORTNAME directly). This form edits
  // just those new columns, same freeform-editing pattern as Finance below.
  const CUSTOMS_FIELDS = [
    'na_type', 'rep_type_cfsp', 'insurance_uplift_maritime', 'insurance_uplift_non_maritime',
    'freight_uplift_air', 'freight_uplift_sea', 'freight_uplift_road', 'guarantee_shortname',
    'supervising_office', 'seed_no', 'trader_excise_no', 'excise_premises_id',
    'aeo_authorisation', 'rex_number', 'deferment_specific_auth', 'descartes_registered_at',
    'descartes_environment', 'misc_notes', 'entry_notes',
  ] as const
  const buildCustomsForm = (c: any): Record<string, string> => Object.fromEntries(CUSTOMS_FIELDS.map(k => {
    const v = c?.[k]
    if (v == null) return [k, '']
    // descartes_registered_at is a TIMESTAMPTZ but edited via a plain <input type="date">, which
    // needs exactly YYYY-MM-DD or it silently shows blank.
    return [k, k === 'descartes_registered_at' ? String(v).slice(0, 10) : String(v)]
  }))
  const [customsForm, setCustomsForm] = useState<Record<string, string>>({})
  const [customsEditing, setCustomsEditing] = useState(false)
  const [savingCustoms, setSavingCustoms] = useState(false)
  const [customsSaved, setCustomsSaved] = useState(false)

  // Contacts
  const [contactEdit, setContactEdit] = useState<Partial<CustomerContact> | null>(null)
  const [savingContact, setSavingContact] = useState(false)
  const [deletingContactId, setDeletingContactId] = useState<string | null>(null)

  // Addresses
  const [addresses, setAddresses] = useState<CustomerAddress[]>([])
  const [addrEdit, setAddrEdit] = useState<Partial<CustomerAddress> | null>(null)
  const [savingAddr, setSavingAddr] = useState(false)
  const [deletingAddrId, setDeletingAddrId] = useState<string | null>(null)
  const [addrError, setAddrError] = useState<string | null>(null)
  // Editing an EXISTING address asks for a confirmation that names the company (see saveAddress); adding a new one does not.
  const [addrConfirm, setAddrConfirm] = useState(false)

  const isCustomerClient = [...selectedIds].some(sid => allTypes.find(a => a.id === sid && a.code === 'Cus'))

  const colorMap = Object.fromEntries(allTypes.map((t, i) => [t.id, CHIP_COLORS[i % CHIP_COLORS.length]]))

  const load = useCallback(async () => {
    if (!id) return
    const [{ data: c }, { data: at }, { data: cat }, { data: cc }, { data: ca }] = await Promise.all([
      supabase.from('customers').select('*').eq('id', id).single(),
      supabase.from('address_types').select('id,code,description,sort_order').eq('is_active', true).order('sort_order'),
      supabase.from('customer_address_types').select('address_type_id').eq('customer_id', id),
      supabase.from('customer_contacts').select('*').eq('customer_id', id).order('sort_order').order('is_primary', { ascending: false }),
      supabase.from('customer_addresses').select('*').eq('customer_id', id).order('address_type').order('is_default', { ascending: false }),
    ])
    if (!c) { navigate('/customers'); return }
    setCustomer(c as any)
    reminder.reset((c as any).reminder ?? '')
    setFinance((c as any).finance ?? {})
    setCustomsForm(buildCustomsForm(c))
    setAllTypes(at || [])
    const ids = new Set<string>((cat || []).map((r: any) => r.address_type_id))
    setSelectedIds(new Set(ids))
    setOriginalIds(new Set(ids))
    setContacts((cc || []) as CustomerContact[])
    setAddresses((ca || []) as CustomerAddress[])
    setLoading(false)
  }, [id, navigate])

  useEffect(() => { load() }, [load])

  // Z73.6.1: quick POA indicator, keyed off this customer's own EORI.
  useEffect(() => {
    let cancelled = false
    setPoaStatus(null)
    if (!customer?.eori) return
    fetchPoaStatusForEori(customer.eori).then(status => { if (!cancelled) setPoaStatus(status) })
    return () => { cancelled = true }
  }, [customer?.eori])

  const toggle = (tid: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(tid) ? next.delete(tid) : next.add(tid)
      return next
    })

  const isDirty = [...selectedIds].some(x => !originalIds.has(x)) || [...originalIds].some(x => !selectedIds.has(x))

  const saveTypes = async () => {
    if (!id) return
    setSavingTypes(true)
    await supabase.from('customer_address_types').delete().eq('customer_id', id)
    if (selectedIds.size > 0) {
      await supabase.from('customer_address_types').insert(
        Array.from(selectedIds).map(tid => ({ customer_id: id, address_type_id: tid }))
      )
    }
    setOriginalIds(new Set(selectedIds))
    setSavingTypes(false)
    setTypeSaved(true)
    setTimeout(() => setTypeSaved(false), 2000)
  }

  const saveFinance = async () => {
    if (!id) return
    setSavingFinance(true)
    await supabase.from('customers').update({ finance }).eq('id', id)
    setSavingFinance(false)
    setFinanceSaved(true)
    setFinanceEditing(false)
    setTimeout(() => setFinanceSaved(false), 2000)
  }

  const saveCustoms = async () => {
    if (!id) return
    setSavingCustoms(true)
    // Empty string -> NULL for every field (numeric/timestamp columns reject '' outright, and
    // text columns should store NULL rather than '' for "not set").
    const payload = Object.fromEntries(CUSTOMS_FIELDS.map(k => [k, customsForm[k]?.trim() ? customsForm[k].trim() : null]))
    const { error } = await supabase.from('customers').update(payload).eq('id', id)
    setSavingCustoms(false)
    if (error) { toast(`Failed to save: ${error.message}`, 'error'); return }
    setCustomsSaved(true)
    setCustomsEditing(false)
    setCustomer(prev => prev ? { ...prev, ...payload } as any : prev)
    setTimeout(() => setCustomsSaved(false), 2000)
  }

  const saveContact = async () => {
    if (!contactEdit || !id) return
    setSavingContact(true)
    try {
      if (contactEdit.id) {
        const { error: cErr } = await supabase.from('customer_contacts').update({
          name: contactEdit.name, role: contactEdit.role,
          email: contactEdit.email || null, phone: contactEdit.phone || null,
          mobile: contactEdit.mobile || null, notes: contactEdit.notes || null,
          is_primary: contactEdit.is_primary,
        }).eq('id', contactEdit.id)
        if (cErr) { window.alert(`Could not save contact: ${cErr.message} (contacts can only be edited by ADMIN / ACCOUNTS / OPERATIONS / SALES)`); return }
      } else {
        const { error: cErr } = await supabase.from('customer_contacts').insert({
          customer_id: id, name: contactEdit.name, role: contactEdit.role,
          email: contactEdit.email || null, phone: contactEdit.phone || null,
          mobile: contactEdit.mobile || null, notes: contactEdit.notes || null,
          is_primary: contactEdit.is_primary ?? false,
        })
        if (cErr) { window.alert(`Could not save contact: ${cErr.message} (contacts can only be edited by ADMIN / ACCOUNTS / OPERATIONS / SALES)`); return }
      }
      setContactEdit(null)
      const { data: cc } = await supabase.from('customer_contacts').select('*').eq('customer_id', id).order('sort_order').order('is_primary', { ascending: false })
      setContacts((cc || []) as CustomerContact[])
    } finally {
      setSavingContact(false)
    }
  }

  const deleteContact = async (cid: string) => {
    if (!window.confirm('Delete this contact?')) return
    setDeletingContactId(cid)
    const { error: dErr } = await supabase.from('customer_contacts').delete().eq('id', cid)
    if (dErr) window.alert(`Could not delete contact: ${dErr.message}`)
    else setContacts(prev => prev.filter(c => c.id !== cid))
    setDeletingContactId(null)
  }

  // Which OTHER address of this company is currently primary for each role (shown when ticking "primary" would replace it).
  const addrLabel = (a: CustomerAddress) => [(a.address_lines ?? [])[0], a.postcode].filter(Boolean).join(', ') || 'another address'
  const takenPrimaryFor = (exceptId?: string): Record<string, string> => {
    const m: Record<string, string> = {}
    for (const a of addresses) {
      if (a.id === exceptId) continue
      for (const r of a.primary_roles ?? []) m[r] = addrLabel(a)
    }
    return m
  }
  // A new address starts as the company's primary for its role only when nobody holds that role yet.
  const emptyAddress = (type = 'operations'): Partial<CustomerAddress> => {
    const d = emptyAddressDraft(type)
    return { ...d, primary_roles: takenPrimaryFor()[type] ? [] : [type] }
  }

  const saveAddress = async (confirmed = false) => {
    if (!addrEdit || !id) return
    if ((addrEdit.city ?? '').length > ADDR_CDS_LIMITS.city) {
      setAddrError(`City exceeds ${ADDR_CDS_LIMITS.city} characters (CDS limit).`)
      return
    }
    if ((addrEdit.postcode ?? '').length > ADDR_CDS_LIMITS.postcode) {
      setAddrError(`Postcode exceeds ${ADDR_CDS_LIMITS.postcode} characters (CDS limit).`)
      return
    }
    if (addrEdit.country && addrEdit.country.length !== 2) {
      setAddrError('Country must be a 2-letter ISO code (CDS limit).')
      return
    }
    if ((addrEdit.roles ?? []).length === 0) {
      setAddrError('Tick at least one use for this address (Operations, Invoice, Registered...).')
      return
    }
    // The same premises typed twice is how duplicate addresses (and duplicates in BoxTop) are born: tick the extra use on the existing one instead.
    if (!addrEdit.id) {
      const key = addressSameKey(addrEdit.address_lines, addrEdit.postcode)
      const same = addresses.find(a => key.split('|')[0] !== '' && addressSameKey(a.address_lines, a.postcode) === key)
      if (same) {
        setAddrError(`This company already has this address (used as: ${(same.roles ?? []).map(r => ADDR_TYPE_META[r]?.label ?? r).join(', ')}). Edit that one and tick the extra use instead of adding it again.`)
        return
      }
    }
    setAddrError(null)
    // Existing address: show "company -- old -> new" first. A new address cannot overwrite anything, so it saves straight away.
    if (addrEdit.id && !confirmed) { setAddrConfirm(true); return }
    setSavingAddr(true)
    try {
      const payload = {
        customer_id: id,
        // roles are the truth; address_type / is_default are mirrored from them by the database.
        roles: sortRoles(addrEdit.roles ?? ['operations']),
        primary_roles: sortRoles((addrEdit.primary_roles ?? []).filter(r => (addrEdit.roles ?? []).includes(r))),
        address_type: sortRoles(addrEdit.roles ?? ['operations'])[0],
        label: addrEdit.label || null,
        address_lines: (addrEdit.address_lines ?? []).map(l => l.trim()).filter(Boolean),
        city: addrEdit.city || null,
        county: addrEdit.county || null,
        postcode: addrEdit.postcode || null,
        country: addrEdit.country || 'GB',
        contact_name: addrEdit.contact_name || null,
        contact_phone: addrEdit.contact_phone || null,
        contact_email: addrEdit.contact_email || null,
        notes: addrEdit.notes || null,
      }
      // The result used to be ignored, so a failed save looked exactly like a successful one.
      const { error: saveErr } = addrEdit.id
        ? await supabase.from('customer_addresses').update(payload).eq('id', addrEdit.id).eq('customer_id', id)
        : await supabase.from('customer_addresses').insert(payload)
      if (saveErr) {
        setAddrConfirm(false)
        setAddrError(`Not saved: ${saveErr.message}`)
        return
      }
      setAddrConfirm(false)
      setAddrEdit(null)
      const { data: ca } = await supabase.from('customer_addresses').select('*').eq('customer_id', id).order('address_type').order('is_default', { ascending: false })
      setAddresses((ca || []) as CustomerAddress[])
    } finally {
      setSavingAddr(false)
    }
  }

  const deleteAddress = async (aid: string) => {
    if (!window.confirm('Delete this address?')) return
    setDeletingAddrId(aid)
    await supabase.from('customer_addresses').delete().eq('id', aid)
    setAddresses(prev => prev.filter(a => a.id !== aid))
    setDeletingAddrId(null)
  }

  // Z19(C): only ADMIN/ACCOUNTS can flip this -- also enforced server-side by a trigger
  // on customer_addresses, since that table's RLS is otherwise open to any authenticated user.
  const toggleFinanceApproved = async (addr: CustomerAddress) => {
    if (!canApproveFinance) return
    const next = !addr.finance_approved
    setAddresses(prev => prev.map(a => a.id === addr.id ? { ...a, finance_approved: next } : a))
    const { error } = await supabase.from('customer_addresses').update({ finance_approved: next }).eq('id', addr.id)
    if (error) {
      setAddresses(prev => prev.map(a => a.id === addr.id ? { ...a, finance_approved: addr.finance_approved } : a))
      toast(error.message, 'error')
    }
  }

  // Z24: manual "Search BoxTop" -- lets staff pre-associate a customer with an existing BoxTop
  // Contact before it's ever synced on a job, instead of only finding out about a possible
  // duplicate mid-sync.
  const searchBoxtop = async () => {
    if (!id) return
    setSearchingBoxtop(true)
    try {
      const result = await matchContact(id)
      if (!result.success) {
        toast(result.error || 'BoxTop search failed', 'error')
      } else if (result.result === 'auto_matched') {
        toast(`Matched to existing BoxTop contact: ${result.accountNumber}`, 'success')
        load()
      } else if (result.result === 'ambiguous_contact_match' && result.candidates?.length && customer) {
        setBoxtopMatchParties([{
          role: 'customer',
          customerId: id,
          customerName: customer.name,
          candidates: result.candidates,
        }])
      } else {
        toast('No similar BoxTop contact found', 'info')
      }
    } finally {
      setSearchingBoxtop(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!customer) return null

  // Z91: the company's country follows its Registered address (DB trigger keeps customers.country_code in
  // sync); with no Registered address it is picked manually here.
  const registeredCountry = (addresses.find(a => a.address_type === 'registered' && a.is_default) ?? addresses.find(a => a.address_type === 'registered'))?.country ?? null
  const saveCompanyCountry = async (code: string) => {
    if (!id || !code) return
    const { error } = await supabase.from('customers').update({ country_code: code, country: code }).eq('id', id)
    if (error) { window.alert(`Could not save country: ${error.message}`); return }
    setCustomer(prev => prev ? { ...prev, country_code: code, country: code } as any : prev)
  }

  const row = (label: string, value?: string | null) => (
    <div key={label}>
      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">{value || '—'}</dd>
    </div>
  )

  const financeField = (key: string, label: string, placeholder?: string) => (
    <div key={key}>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      {financeEditing ? (
        <input type="text" value={finance[key] ?? ''} placeholder={placeholder}
          onChange={e => setFinance(prev => ({ ...prev, [key]: e.target.value }))}
          className={`mt-0.5 ${inputCls}`} />
      ) : (
        <p className="mt-0.5 text-sm text-gray-900 dark:text-white">{finance[key] || '—'}</p>
      )}
    </div>
  )

  const customsField = (key: string, label: string, opts?: { type?: string; maxLength?: number; textarea?: boolean; options?: string[] }) => (
    <div key={key}>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      {customsEditing ? (
        opts?.options ? (
          <select value={customsForm[key] ?? ''} onChange={e => setCustomsForm(prev => ({ ...prev, [key]: e.target.value }))} className={`mt-0.5 ${inputCls}`}>
            <option value="">—</option>
            {opts.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : opts?.textarea ? (
          <textarea value={customsForm[key] ?? ''} rows={2}
            onChange={e => setCustomsForm(prev => ({ ...prev, [key]: e.target.value }))}
            className={`mt-0.5 ${inputCls}`} />
        ) : (
          <input type={opts?.type ?? 'text'} value={customsForm[key] ?? ''} maxLength={opts?.maxLength}
            onChange={e => setCustomsForm(prev => ({ ...prev, [key]: e.target.value }))}
            className={`mt-0.5 ${inputCls}`} />
        )
      ) : (
        <p className="mt-0.5 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{customsForm[key] || '—'}</p>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Breadcrumb + header */}
        <div className="mb-6">
          <Link to="/customers" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">← Companies</Link>
          <div className="mt-2 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white font-mono">{customer.code}</h1>
                {/* A9/Z52: boxtop_account_number already drove the Sync/Search button below, but
                    was never shown anywhere -- staff had no way to see which BoxTop AccNum a
                    customer is bound to without opening the sync log. */}
                {BOXTOP_SYNC_ENABLED && customer.boxtop_account_number && (
                  <span
                    title="BoxTop Account Number"
                    className="px-2 py-0.5 text-xs font-mono font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                  >
                    BoxTop: {customer.boxtop_account_number}
                  </span>
                )}
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 mt-0.5">{customer.name}</p>
              <div className="flex gap-2 mt-2">
                {customer.is_active ? (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Active</span>
                ) : (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Inactive</span>
                )}
                {customer.is_on_hold && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">ON HOLD</span>
                )}
                {isCustomerClient && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">Customer/Client</span>
                )}
                {poaStatus && (
                  <PoaStatusBadge badge={poaStatus.badge} title={formatPoaStatusLine(poaStatus, customer.eori)} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {BOXTOP_SYNC_ENABLED && !customer.boxtop_account_number && (
                <button onClick={searchBoxtop} disabled={searchingBoxtop}
                  title="Look up this company in BoxTop's Contact list to pre-associate it before syncing"
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium disabled:opacity-50">
                  {searchingBoxtop ? 'Searching…' : 'Search BoxTop'}
                </button>
              )}
              <button onClick={() => setEditOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                Edit
              </button>
            </div>
          </div>
        </div>

        {BOXTOP_SYNC_ENABLED && (<>
        {/* Z102 PR1: the BoxTop account code is a first-class mapping (every BoxTop address lookup hangs off
            it), so it is shown with its verification state and can be verified / corrected right here. */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">BoxTop account</h2>
          <BoxtopAccountPanel
            customerId={id!}
            customerName={customer.name}
            customerCode={customer.code}
            accountNumber={customer.boxtop_account_number ?? null}
            saved={{
              status: customer.boxtop_account_check_status ?? null,
              checkedNumber: customer.boxtop_account_checked_number ?? null,
              checkedAt: customer.boxtop_account_checked_at ?? null,
              checkedBy: customer.boxtop_account_checked_by ?? null,
              companyName: customer.boxtop_account_company_name ?? null,
              addressCount: customer.boxtop_account_address_count ?? null,
            }}
            nameConfirmed={customer.boxtop_account_name_confirmed_number && customer.boxtop_account_name_confirmed_number === customer.boxtop_account_number
              ? { by: customer.boxtop_account_name_confirmed_by ?? null, at: customer.boxtop_account_name_confirmed_at ?? null, boxtopName: customer.boxtop_account_name_confirmed_boxtop_name ?? null }
              : null}
            onChanged={load}
          />
        </div>
        </>)}

        {/* Reminder / Special Instructions */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
              <span>⚠</span> Reminder / Special Instructions
            </h2>
            <div className="flex items-center gap-2">
              <AutosaveStatusLabel status={reminder.status} />
              <button onClick={reminder.saveNow} disabled={reminder.status === 'saving'}
                className="px-3 py-1 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                Save
              </button>
            </div>
          </div>
          <textarea
            value={reminder.value}
            onChange={e => reminder.onChange(e.target.value)}
            rows={3}
            placeholder="E.g. Delivery 9am–3pm only. Always include EORI on paperwork. Strict pallet requirements."
            className="w-full px-3 py-2 text-sm border border-amber-300 dark:border-amber-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
          />
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">This note will appear as a banner when opening any job for this company.</p>
        </div>

        {/* Address Types */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Address Types / Roles</h2>
            {isDirty && (
              <button onClick={saveTypes} disabled={savingTypes}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {savingTypes ? 'Saving…' : 'Save'}
              </button>
            )}
            {typeSaved && !isDirty && <span className="text-sm text-green-600 font-medium">Saved ✓</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {allTypes.map(at => {
              const checked = selectedIds.has(at.id)
              return (
                <button key={at.id} type="button" onClick={() => toggle(at.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                    checked ? `${colorMap[at.id]} border-current` : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:border-gray-400'
                  }`}>
                  {at.description}{checked && <span className="ml-1.5 opacity-60">✓</span>}
                </button>
              )
            })}
          </div>
          {allTypes.length === 0 && (
            <p className="text-sm text-gray-400">No address types configured — ask an Admin to add them under Admin → Address Types.</p>
          )}
        </div>

        {/* Company info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Company Information</h2>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
            {row('Code', customer.code)}
            {row('Currency', customer.currency)}
            <div key="country">
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Country</dt>
              <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
                {registeredCountry ? (
                  <span>{countryLabelForCode(registeredCountry)} <span className="text-xs text-gray-400">(from Registered address)</span></span>
                ) : (
                  <>
                    <CountrySelect value={(customer as any).country_code ?? customer.country ?? ''} onChange={saveCompanyCountry} />
                    <span className="text-xs text-gray-400">No Registered address yet — pick the country of registration manually.</span>
                  </>
                )}
              </dd>
            </div>
            {row('Branch', customer.branch)}
            {row('Payment Terms', customer.payment_terms)}
            {row('Credit Limit', customer.credit_limit != null ? `${customer.currency} ${Number(customer.credit_limit).toLocaleString()}` : undefined)}
          </dl>
        </div>

        {/* Addresses */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">Addresses</h2>

          {/* ONE list: every address once, with everything it is used as. The same premises is never typed per role. */}
          {addresses.length === 0 ? (
            <p className="text-sm text-gray-400">
              No address yet.{' '}
              <button className="text-blue-600 hover:underline" onClick={() => { setAddrError(null); setAddrEdit(emptyAddress('operations')) }}>Add the first address →</button>
            </p>
          ) : (
            <div className="space-y-2">
              {[...addresses].sort((x, y) => sortRoles(x.roles ?? [x.address_type])[0].localeCompare(sortRoles(y.roles ?? [y.address_type])[0])).map(addr => (
                <div key={addr.id} className="flex items-start gap-4 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      {(addr.roles ?? [addr.address_type]).map(r => (
                        <span key={r} className={`text-xs font-semibold px-2 py-0.5 rounded ${ADDR_TYPE_META[r]?.color ?? 'bg-gray-100 text-gray-700'}`}
                          title={(addr.primary_roles ?? []).includes(r) ? `The company's primary ${ADDR_TYPE_META[r]?.label.toLowerCase()} address` : `Used as ${ADDR_TYPE_META[r]?.label.toLowerCase()} (not the primary one)`}>
                          {ADDR_TYPE_META[r]?.label ?? r}{(addr.primary_roles ?? []).includes(r) ? ' ★' : ''}
                        </span>
                      ))}
                      {addr.label && <span className="font-medium text-sm text-gray-900 dark:text-white ml-1">{addr.label}</span>}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {[...(addr.address_lines ?? []), addr.city, addr.county, addr.postcode, addr.country !== 'GB' ? addr.country : null]
                        .filter(l => l && l.trim().length > 0)
                        .join(', ') || '—'}
                    </p>
                    {(addr.contact_name || addr.contact_phone || addr.contact_email) && (
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                        {addr.contact_name && <span>{addr.contact_name}</span>}
                        {addr.contact_phone && <span>T: {addr.contact_phone}</span>}
                        {addr.contact_email && <span>{addr.contact_email}</span>}
                      </div>
                    )}
                    {addr.notes && <p className="mt-0.5 text-xs text-gray-400 italic">{addr.notes}</p>}
                    {(addr.roles ?? [addr.address_type]).includes('invoice') && (
                      <label className={`mt-1.5 flex items-center gap-1.5 text-xs ${canApproveFinance ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400'}`}
                        title={canApproveFinance ? undefined : 'Only Finance/Admin can change this'}>
                        <input type="checkbox" checked={addr.finance_approved} disabled={!canApproveFinance}
                          onChange={() => toggleFinanceApproved(addr)}
                          className="rounded border-gray-300 disabled:opacity-60" />
                        Finance Approved
                      </label>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => { setAddrError(null); setAddrEdit({ ...addr }) }} className="text-xs text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => deleteAddress(addr.id)} disabled={deletingAddrId === addr.id}
                      className="text-xs text-red-500 hover:underline disabled:opacity-50">
                      {deletingAddrId === addr.id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Roles with no primary address: say what jobs will do instead, so nobody is surprised */}
          {addresses.length > 0 && (() => {
            const missing = ['operations', 'invoice'].filter(r => !addresses.some(a => (a.primary_roles ?? []).includes(r)))
            if (missing.length === 0) return null
            return (
              <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                {missing.map(r => ADDR_TYPE_META[r].label).join(' and ')}: no primary address set.
                {missing.includes('invoice') && !missing.includes('operations') && ' Jobs that need an Invoice address will use the Operations address (and say so).'}
                {addresses.length > 1 && ' Edit an address and tick "Primary" for the role.'}
              </p>
            )
          })()}

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button onClick={() => { setAddrError(null); setAddrEdit(emptyAddress('operations')) }}
              className="text-sm text-blue-600 hover:underline">
              + Add address
            </button>
          </div>
        </div>

        {/* Z73.19: Customs (Descartes) -- na_registry retired, declaration generation now reads
            customers.code directly as the Descartes SHORTNAME (see the Code field in the header
            above) plus these fields for NA registration/customs data. */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-orange-200 dark:border-orange-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Customs (Descartes)</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Declarations use this customer's Code ({customer.code}) as the Descartes SHORTNAME directly -- no separate link needed.
                The fields below are for Descartes NA registration and customs-specific data.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {customsSaved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
              {customsEditing ? (
                <>
                  <button onClick={saveCustoms} disabled={savingCustoms}
                    className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {savingCustoms ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => { setCustomsEditing(false); setCustomsForm(buildCustomsForm(customer)) }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                </>
              ) : (
                <button onClick={() => setCustomsEditing(true)} className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:underline">Edit</button>
              )}
            </div>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            {customsField('na_type', 'NA Type', { options: ['CDS', 'XX', 'WH'] })}
            {customsField('rep_type_cfsp', 'Rep Type (CFSP)', { maxLength: 1 })}
            {customsField('deferment_specific_auth', 'Deferment Specific Auth', { maxLength: 1 })}
            {customsField('guarantee_shortname', 'Guarantee Shortname', { maxLength: 18 })}
            {customsField('supervising_office', 'Supervising Office', { maxLength: 18 })}
            {customsField('seed_no', 'SEED No.', { maxLength: 16 })}
            {customsField('trader_excise_no', 'Trader Excise No.', { maxLength: 16 })}
            {customsField('excise_premises_id', 'Excise Premises ID', { maxLength: 16 })}
            {customsField('aeo_authorisation', 'AEO Authorisation', { maxLength: 35 })}
            {customsField('rex_number', 'REX Number', { maxLength: 35 })}
            {customsField('insurance_uplift_maritime', 'Insurance Uplift (Maritime)', { type: 'number' })}
            {customsField('insurance_uplift_non_maritime', 'Insurance Uplift (Non-Maritime)', { type: 'number' })}
            {customsField('freight_uplift_air', 'Freight Uplift (Air)', { type: 'number' })}
            {customsField('freight_uplift_sea', 'Freight Uplift (Sea)', { type: 'number' })}
            {customsField('freight_uplift_road', 'Freight Uplift (Road)', { type: 'number' })}
            {customsField('descartes_environment', 'Descartes Environment', { options: ['UAT', 'PROD'] })}
            {customsField('descartes_registered_at', 'Descartes Registered At', { type: 'date' })}
            <div className="col-span-2 sm:col-span-3">{customsField('misc_notes', 'Misc Notes', { textarea: true })}</div>
            <div className="col-span-2 sm:col-span-3">{customsField('entry_notes', 'Entry Notes', { textarea: true })}</div>
          </dl>
        </div>

        {/* Finance section — only visible when Customer/Client role is selected */}
        {isCustomerClient && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200 dark:border-purple-700 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Finance</h2>
                <p className="text-xs text-gray-400 mt-0.5">Invoicing details for this Customer/Client</p>
              </div>
              <div className="flex items-center gap-2">
                {financeSaved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
                {financeEditing ? (
                  <>
                    <button onClick={saveFinance} disabled={savingFinance}
                      className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {savingFinance ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setFinanceEditing(false)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setFinanceEditing(true)} className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:underline">Edit</button>
                )}
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
              {financeField('vat_number', 'VAT Number', 'GB123456789')}
              {financeField('invoice_address', 'Invoice Address', 'Same as main or different')}
              {financeField('payment_terms', 'Payment Terms', 'NET 30 / COD / etc.')}
              {financeField('credit_limit', 'Credit Limit', '10000')}
              {financeField('bank_name', 'Bank Name', 'Barclays')}
              {financeField('bank_account', 'Bank Account', 'Sort code — Account no.')}
              {financeField('finance_contact_name', 'Finance Contact', 'Name')}
              {financeField('finance_contact_email', 'Finance Email', 'finance@company.com')}
            </dl>
          </div>
        )}

        {/* Contacts */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Contacts</h2>
            <button onClick={() => setContactEdit(emptyContact())}
              className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              + Add Contact
            </button>
          </div>

          {contacts.length === 0 ? (
            <p className="text-sm text-gray-400">No contacts yet. <button className="text-blue-600 hover:underline" onClick={() => setContactEdit(emptyContact())}>Add the first one →</button></p>
          ) : (
            <div className="space-y-3">
              {contacts.map(c => (
                <div key={c.id} className="flex items-start gap-4 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 dark:text-white">{c.name || '(no name)'}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">{c.role}</span>
                      {c.is_primary && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">Primary</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                      {c.email && <span>{c.email}</span>}
                      {c.phone && <span>T: {c.phone}</span>}
                      {c.mobile && <span>M: {c.mobile}</span>}
                      {c.notes && <span className="italic text-gray-400">{c.notes}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => setContactEdit({ ...c })} className="text-xs text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => deleteContact(c.id)} disabled={deletingContactId === c.id}
                      className="text-xs text-red-500 hover:underline disabled:opacity-50">
                      {deletingContactId === c.id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Address edit modal */}
      {addrEdit !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{addrEdit.id ? 'Edit Address' : 'Add Address'}</h3>
            {/* The company is named up front on purpose: the damage this guards against is editing the address of a different company than the one in your head. */}
            <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-blue-700 dark:text-blue-300">Company</p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">{customer.name} <span className="font-mono text-sm text-gray-500 dark:text-gray-400">({customer.code})</span></p>
            </div>
            {addrError && (
              <div className="mb-3 p-2.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-xs">
                {addrError}
              </div>
            )}
            <CustomerAddressFields
              value={addrEdit}
              onChange={patch => setAddrEdit(p => ({ ...p!, ...patch }))}
              showRoles takenPrimary={takenPrimaryFor(addrEdit.id)} showLabel showContact showNotes
            />
            {addrConfirm && addrEdit.id && (() => {
              const fmt = (a: { address_lines?: string[] | null; city?: string | null; county?: string | null; postcode?: string | null; country?: string | null } | undefined) =>
                [...((a?.address_lines ?? []).map(l => l.trim()).filter(Boolean)), a?.city, a?.county, a?.postcode, a?.country].filter(Boolean).join(', ') || '(empty)'
              const before = addresses.find(a => a.id === addrEdit.id)
              return (
                <div className="mb-4 rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">You are about to change the address of</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">{customer.name} <span className="font-mono text-sm font-normal text-gray-500">({customer.code})</span></p>
                  <p className="text-xs text-gray-500 mb-0.5">Now</p>
                  <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">{fmt(before)}</p>
                  <p className="text-xs text-gray-500 mb-0.5">Will become</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{fmt(addrEdit)}</p>
                </div>
              )
            })()}
            <div className="flex justify-end gap-2">
              {addrConfirm ? (
                <>
                  <button onClick={() => setAddrConfirm(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Back</button>
                  <button onClick={() => saveAddress(true)} disabled={savingAddr}
                    className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                    {savingAddr ? 'Saving…' : 'Confirm and save'}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => { setAddrError(null); setAddrConfirm(false); setAddrEdit(null) }} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={() => saveAddress()} disabled={savingAddr}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {savingAddr ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact edit modal */}
      {contactEdit !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{contactEdit.id ? 'Edit Contact' : 'New Contact'}</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                <input type="text" value={contactEdit.name ?? ''} onChange={e => setContactEdit(p => ({ ...p!, name: e.target.value }))}
                  className={inputCls} placeholder="Full name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
                <select value={contactEdit.role ?? 'General'} onChange={e => setContactEdit(p => ({ ...p!, role: e.target.value }))}
                  className={inputCls}>
                  {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={contactEdit.is_primary ?? false}
                    onChange={e => setContactEdit(p => ({ ...p!, is_primary: e.target.checked }))} className="rounded" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Primary contact</span>
                </label>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input type="email" value={contactEdit.email ?? ''} onChange={e => setContactEdit(p => ({ ...p!, email: e.target.value }))}
                  className={inputCls} placeholder="email@company.com" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                <input type="text" value={contactEdit.phone ?? ''} onChange={e => setContactEdit(p => ({ ...p!, phone: e.target.value }))}
                  className={inputCls} placeholder="+44 121 000 0000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Mobile</label>
                <input type="text" value={contactEdit.mobile ?? ''} onChange={e => setContactEdit(p => ({ ...p!, mobile: e.target.value }))}
                  className={inputCls} placeholder="+44 7700 000000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                <input type="text" value={contactEdit.notes ?? ''} onChange={e => setContactEdit(p => ({ ...p!, notes: e.target.value }))}
                  className={inputCls} placeholder="Any special notes…" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setContactEdit(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={saveContact} disabled={savingContact || !contactEdit.name?.trim()}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingContact ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <CustomerModal isOpen={editOpen} onClose={() => setEditOpen(false)}
          customer={customer} onSaved={() => { setEditOpen(false); load() }} />
      )}

      {BOXTOP_SYNC_ENABLED && boxtopMatchParties && (
        <BoxtopContactMatchDialog
          parties={boxtopMatchParties}
          confirmLabel="Confirm"
          onCancel={() => setBoxtopMatchParties(null)}
          onResolved={() => { setBoxtopMatchParties(null); load() }}
        />
      )}
    </div>
  )
}
